import { Injectable, computed, signal } from '@angular/core';
import { EInvoiceViewerSearchHandler } from '../interfaces/einvoice-viewer-search-handler.interface';
import { EInvoiceViewerSearchOptions } from '../interfaces/einvoice-viewer-search-options.interface';
import { EInvoiceViewerView } from '../interfaces/einvoice-viewer-view.type';

@Injectable()
export class EinvoiceViewerSearchService {
  private static readonly SCROLL_DEBOUNCE_MS = 250;
  private readonly querySignal = signal('');
  private readonly exactMatchSignal = signal(false);
  private readonly activeViewSignal = signal<EInvoiceViewerView>('document');
  private readonly handlersRevision = signal(0);
  private readonly handlers = new Map<EInvoiceViewerView, EInvoiceViewerSearchHandler>();
  private scrollDebounceHandle: ReturnType<typeof setTimeout> | undefined;

  readonly query = this.querySignal.asReadonly();
  readonly exactMatch = this.exactMatchSignal.asReadonly();
  readonly options = computed<EInvoiceViewerSearchOptions>(() => ({
    query: this.querySignal(),
    exactMatch: this.exactMatchSignal()
  }));
  readonly activeView = this.activeViewSignal.asReadonly();
  readonly activeHandler = computed(() => {
    this.handlersRevision();
    return this.handlers.get(this.activeViewSignal());
  });
  readonly searchVisible = computed(() => this.activeHandler() !== undefined);
  readonly matchCount = computed(() => this.activeHandler()?.matchCount() ?? 0);
  readonly activeMatchIndex = computed(() => this.activeHandler()?.activeMatchIndex() ?? -1);
  readonly canNavigate = computed(() => this.matchCount() > 0);
  readonly searchPosition = computed(() => {
    const query = this.querySignal().trim();
    const matchCount = this.matchCount();

    if (!query) {
      return '';
    }

    if (matchCount === 0) {
      return '0 / 0';
    }

    return `${this.activeMatchIndex() + 1} / ${matchCount}`;
  });

  registerHandler(view: EInvoiceViewerView, handler: EInvoiceViewerSearchHandler): void {
    this.handlers.set(view, handler);
    handler.setSearchQuery(this.options());
    this.handlersRevision.update(revision => revision + 1);
  }

  unregisterHandler(view: EInvoiceViewerView, handler: EInvoiceViewerSearchHandler): void {
    if (this.handlers.get(view) !== handler) {
      return;
    }

    this.handlers.delete(view);
    this.handlersRevision.update(revision => revision + 1);
  }

  setActiveView(view: EInvoiceViewerView): void {
    this.cancelScheduledScroll();
    this.activeViewSignal.set(view);
  }

  setSearchQuery(query: string): void {
    this.querySignal.set(query);
    this.activeHandler()?.setSearchQuery(this.options());
    this.scheduleScrollToFirstResult();
  }

  setExactMatch(exactMatch: boolean): void {
    this.exactMatchSignal.set(exactMatch);
    this.activeHandler()?.setSearchQuery(this.options());
    this.scheduleScrollToFirstResult();
  }

  nextSearchResult(): void {
    this.cancelScheduledScroll();
    this.activeHandler()?.nextSearchResult();
  }

  previousSearchResult(): void {
    this.cancelScheduledScroll();
    this.activeHandler()?.previousSearchResult();
  }

  submitSearch(): void {
    this.cancelScheduledScroll();
    this.activeHandler()?.scrollToActiveResult();
  }

  reset(): void {
    this.cancelScheduledScroll();
    this.querySignal.set('');
    this.exactMatchSignal.set(false);
    for (const handler of this.handlers.values()) {
      handler.setSearchQuery(this.options());
    }
  }

  private scheduleScrollToFirstResult(): void {
    this.cancelScheduledScroll();
    const handler = this.activeHandler();
    if (!handler) {
      return;
    }

    this.scrollDebounceHandle = setTimeout(() => {
      this.scrollDebounceHandle = undefined;
      handler.scrollToActiveResult();
    }, EinvoiceViewerSearchService.SCROLL_DEBOUNCE_MS);
  }

  private cancelScheduledScroll(): void {
    if (this.scrollDebounceHandle !== undefined) {
      clearTimeout(this.scrollDebounceHandle);
      this.scrollDebounceHandle = undefined;
    }
  }
}